<template>
    <div data-component="OrderSummaryCard"
         class="order-summary-card">
        <div class="order-summary-card__header">
            <div data-testid="summaryTitle"
                 class="order-summary-card__title">
                {{ title }}
            </div>

            <div data-testid="summaryTotals"
                 class="order-summary-card__totals">
                <slot name="totals"></slot>
            </div>
        </div>

        <div class="order-summary-card__body">
            <div class="order-summary-card__rows">
                <slot name="rows"></slot>
            </div>

            <div v-if="$slots.footer"
                 class="order-summary-card__footer">
                <slot name="footer"></slot>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * A summary box at the top of an order details tab.
 * Same component used by both the ticket and membership summary.
 */
export default {
    name: 'OrderSummaryCard',

    props: {
        title: {
            type: String,
            required: true,
        },
    },
};
</script>

<style lang="less">
@import "../../../../../less/variables";

@order-summary-card-divider: rgba(255, 255, 255, 0.15);

.order-summary-card {
    // BG is lighter than the actual groups.
    background: fade(@mid-blue, 15%);
    border-radius: @border-radius-base;
}

// The header has the same size and spacing as an item group's header.
.order-summary-card__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: @gutter-width;
    padding: @gutter-width;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: @border-radius-base @border-radius-base 0 0;
}

.order-summary-card__title {
    font-size: 20px;
    font-weight: bold;
    margin-top: -4px; // Negate some whitespace at the top of the element caused by line-height.
}

.order-summary-card__totals {
    color: @text-color-muted-on-dark;
    white-space: nowrap;
}

.order-summary-card__body {
    padding: @gutter-width;
}

.order-summary-card__rows {
    &:not(:empty) {
        margin-top: -@gutter-width;
    }
}

.order-summary-card__row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: @gutter-width;
    // The negative margins cancel out the horizontal padding, so the row text lines up with the
    // card heading while the dividers and hover highlight still run the full width of the card.
    margin-left: -@gutter-width;
    margin-right: -@gutter-width;
    padding: 10px @gutter-width;
    font-size: 15px;

    &:not(:first-child) {
        border-top: 1px solid @order-summary-card-divider;
    }
}

// Only rows that go somewhere when clicked get the pointer and highlight.
.order-summary-card__row--clickable {
    cursor: pointer;

    &:hover,
    &:focus {
        background: @table-row-bg-hover;
    }
}

.order-summary-card__row-main {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: @half-gutter-width;
}

.order-summary-card__row-meta {
    display: flex;
    align-items: baseline;
    gap: @gutter-width;
    white-space: nowrap;
}

.order-summary-card__row-time {
    color: @text-color-muted-on-dark;
}

.order-summary-card__row-redemptions {
    color: @text-color-muted-on-dark;
    &--redeemed {
        color: @dt-green-300;
        font-weight: bold;
    }
}

.order-summary-card__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: @gutter-width;
    padding-top: @gutter-width;
    border-top: 1px solid @order-summary-card-divider;
}

@media (max-width: @mobile-max) {
    .order-summary-card {
        background: @dt-grey-neutral-100;
        // Same box treatment as the item groups, which are barely lighter than the page without it.
        border: 1px solid @dt-grey-neutral-300;
        overflow: hidden; // Keep the header's corners inside the rounded border.
    }

    .order-summary-card__totals,
    .order-summary-card__row-time,
    .order-summary-card__row-redemptions {
        color: @dt-grey-neutral-700;
    }

    // Not enough room for the totals next to the title, so they go underneath it.
    .order-summary-card__header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0;
    }

    .order-summary-card__totals {
        margin-top: 2px;
        white-space: normal;
    }

    .order-summary-card__row {
        // Stack, so the redemption count is always on its own line rather than only wrapping onto
        // one when the name and badges happen to be wide enough to push it there.
        flex-direction: column;
        align-items: flex-start;
        gap: 0; // The gap is for the space between the name and the count side by side.
        &:not(:first-child) {
            border-top-color: rgba(0, 0, 0, 0.12);
        }
    }

    .order-summary-card__row--clickable {
        &:hover,
        &:focus {
            background: rgba(0, 0, 0, 0.05);
        }
    }

    .order-summary-card__row-meta {
        margin-top: 2px;
        font-size: @font-size-small;
    }

    .order-summary-card__row-redemptions--redeemed {
        color: @dt-green-800;
    }

    .order-summary-card__footer {
        // Stretch so the redeem button is full width, which is easier to hit on a phone.
        align-items: stretch;
        border-top-color: rgba(0, 0, 0, 0.12);
    }
}
</style>
